<template>
  <div>
    <div v-if="documents.length > 0" class="list-actions">
      <div class="select">
        <ts-link @click="handleSelectAllClick">Select all</ts-link>
        <ts-link @click="handleUnselectAllClick">Unselect all</ts-link>
      </div>
      <div class="actions" v-show="hasSelectedDocuments">
        <ts-button size="small" @click="handleDeleteDocumentsClick(selectedDocuments)"> Delete </ts-button>
        <ts-button
          :pending="!canDownloadDocuments"
          size="small"
          @click="handleDownloadDocumentsClick(selectedDocuments)"
        >
          Download
        </ts-button>
      </div>
    </div>
    <div ref="droptarget" class="droptarget" @dragover="isDragging = true" @dragleave="isDragging = false">
      <div class="list">
        <div v-for="document in documents" :key="document.id" class="row">
          <ts-checkbox
            class="checkbox"
            :value="isDocumentSelected(document)"
            @input="handleDocumentSelectedChange(document, $event)"
          ></ts-checkbox>
          <div class="file-info">
            <p class="file-name">
              {{ document.name }}
            </p>
            <div v-if="isDocumentUploading(document)">
              <ts-progress :percentage="document.resumableFile.tsUploadProgress * 100"></ts-progress>
            </div>
            <p v-else class="file-creation-info">
              Created on {{ formatDate(document.createdAt) }} by {{ document.createdBy.name }}
            </p>
          </div>
          <div class="actions">
            <div v-if="!isDocumentUploading(document)" class="actions">
              <ts-button
                :pending="isPending(document.status)"
                size="small"
                circle
                @click="handleDownloadDocumentClick(document)"
              >
                <ts-icon
                  :title="isPending(document.status) ? 'Pending' : 'Download'"
                  :pending="isPending(document.status)"
                  icon="save"
                  size="x-small"
                  color="#6d7587"
                />
              </ts-button>
              <FilePreview
                v-if="isFilePreviewable(document.file)"
                :file="document.file"
                :show-edit="false"
                @delete="handleDeleteDocumentsClick([document])"
              />
              <EditDocument :value="document" @input="handleDocumentEdit" />
            </div>
            <ts-button size="small" circle @click="handleDeleteDocumentsClick([document])">
              <ts-icon icon="trash" size="x-small" color="#6d7587" title="Delete" />
            </ts-button>
          </div>
        </div>
      </div>
      <div v-if="canUpload" ref="droptargetPlaceholder" class="placeholder">
        <DropTarget :label="label" :active="isDragging" :show-dropin="documents.length === 0" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.list-actions {
  margin-bottom: 20px;
  display: flex;

  .select {
    flex-grow: 1;

    > * {
      margin-right: 23px;
    }
  }

  .actions {
    > * {
      margin-left: 8px;
    }
  }
}

.droptarget {
  .placeholder {
    display: flex;
    justify-content: center;
    margin: 50px;
  }
}

.row {
  background: #ebecf0;
  background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
  background-blend-mode: soft-light, normal;
  background-clip: padding-box;
  border: 2px solid rgba(255, 255, 255, 0.16);
  box-shadow: -2px -2px 6px 0 rgba(250, 251, 255, 0.48), 2px 2px 6px 0 rgba(166, 171, 189, 0.24);
  border-radius: 16px;
  margin-bottom: 10px;

  display: flex;
  align-items: center;
  padding: 22px 18px;

  .checkbox {
    margin-right: 13px;
  }

  .file-info {
    flex-grow: 1;
  }

  .file-name {
    font-family: Roboto;
    font-size: 16px;
    color: #6d7587;
    overflow-wrap: break-word;
  }

  .file-creation-info {
    font-family: Roboto;
    font-size: 10px;
    line-height: 16px;
    color: #acbacc;
  }

  .actions {
    display: flex;
    align-items: center;

    > * {
      margin-left: 10px;
    }
  }
}
</style>

<script>
import EditDocument from './EditDocument.vue';
import DropTarget from '@/components/DropTarget.vue';
import FilePreview from '@/components/FilePreview.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';
import uploader from '@/mixins/uploader';

export default {
  mixins: [projectViewHelpers, uploader],
  components: {
    EditDocument,
    DropTarget,
    FilePreview
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    documents: {
      type: Array,
      default: () => []
    },
    canUpload: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      selectedDocuments: [],
      isDragging: false
    };
  },
  watch: {
    documents() {
      this.selectedDocuments = [];
    }
  },
  computed: {
    hasSelectedDocuments() {
      return this.selectedDocuments.length > 0;
    },
    canDownloadDocuments() {
      return (
        this.selectedDocuments.length > 0 &&
        this.selectedDocuments.every(document => {
          return document.file?.status === 'processed';
        })
      );
    }
  },
  methods: {
    isDocumentSelected(document) {
      return !!this.selectedDocuments.find(d => d.id === document.id);
    },
    isDocumentUploading(document) {
      return document.resumableFile && !document.resumableFile.tsIsComplete;
    },
    handleSelectAllClick() {
      this.selectedDocuments = this.documents;
    },
    handleUnselectAllClick() {
      this.selectedDocuments = [];
    },
    handleDocumentSelectedChange(document, value) {
      if (value) {
        this.selectedDocuments = [...this.selectedDocuments, document];
      } else {
        this.selectedDocuments = this.selectedDocuments.filter(d => d.id !== document.id);
      }
    },
    handleDownloadDocumentClick(document) {
      if (this.isPending(document.status)) {
        window.EventBus.$emit('download.pending');
        return;
      }
      window.open(document.file.url, '_blank');
    },
    handleDocumentEdit(document) {
      this.$emit('document-edit', document);
    },
    handleDeleteDocumentsClick(documents) {
      documents
        .filter(document => !!document.resumableFile)
        .forEach(document => {
          this.resumable.removeFile(document.resumableFile);
          this.$emit('document-delete', document);
        });
      documents
        .filter(document => !document.resumableFile)
        .forEach(document => {
          this.$emit('document-delete', document);
        });
    },
    handleDownloadDocumentsClick(documents) {
      if (!this.canDownloadDocuments) {
        window.EventBus.$emit('download.pending');
        return;
      }
      const uploadedDocuments = documents.filter(document => !document.resumableFile);
      if (uploadedDocuments.length > 1) {
        this.showDownloadProcessing();
        this.$emit('documents-download', uploadedDocuments);
        return;
      }

      uploadedDocuments.length === 1 && this.handleDownloadDocumentClick(uploadedDocuments[0]);
    }
  },
  mounted() {
    if (this.canUpload) {
      this.resumable.assignBrowse(this.$refs.droptargetPlaceholder);
      this.resumable.assignDrop(this.$refs.droptarget);
    }
    this.resumable.on('fileAdded', file => {
      this.$emit('file-added', file);
      this.resumable.upload();
    });
    this.resumable.on('fileSuccess', file => {
      this.$emit('file-success', file);
    });
    this.resumable.on('fileError', (file, message) => {
      this.$emit('file-error', file, message);
    });
  }
};
</script>
